import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../Assets/Images/landing/eww-logo.png";
import Profile from "../Profile/profile";
import SignIn from "../SignIn/signIn";
import SettingsModal from "../SettingsModal/settingsModal";
import type { RootState } from "../../Redux/store";
import { useTranslation } from "react-i18next";

/**
 * Desktop-only floating nav for the redesigned landing page (hidden on mobile via
 * `d-lg-block d-none`, since Header.tsx's own mobile menu -- HeaderMobileView -- keeps
 * rendering unconditionally on small screens for every route, same pattern /home and
 * /addtemplate already rely on). The global Header's desktop bar is suppressed for "/" in
 * Header.tsx's `condition()` so this replaces it instead of stacking a second navbar.
 */
const Nav: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth ?? {}).length);
  const avatarAnchorRef = useRef<HTMLDivElement>(null);
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const { t } = useTranslation();

  const handleStarted = () => {
    const win = window as Window & { opera?: string; MSStream?: unknown };
    const userAgent = navigator.userAgent || navigator.vendor || win.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent as string) && !win.MSStream;
    const isAndroid = /Android/.test(userAgent as string);

    if (isIOS) {
      window.location.href =
        "https://apps.apple.com/us/app/ezy-watermark-photos-lite/id494473910";
    } else if (isAndroid) {
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.whizpool.ezywatermarklite";
    } else {
      history.push("/home");
    }
  };

  return (
    <header className="lr-nav d-lg-block d-none">
      <div className="lr-nav__bar">
        <a href="/" className="lr-nav__brand" onClick={(e) => { e.preventDefault(); history.push("/"); }}>
          <img src={logo} alt="eZy Watermark" />
          eZy Watermark
        </a>
        <nav className="lr-nav__links">
          <button
            type="button"
            className={location.pathname === "/features" ? "active" : ""}
            onClick={() => history.push("/features")}
          >
            Features
          </button>
          <button
            type="button"
            className={location.pathname === "/pricing" ? "active" : ""}
            onClick={() => history.push("/pricing")}
          >
            Pricing
          </button>
          <button
            type="button"
            className={location.pathname === "/faq" ? "active" : ""}
            onClick={() => history.push("/faq")}
          >
            FAQs
          </button>
          <button
            type="button"
            className={location.pathname === "/blogs" ? "active" : ""}
            onClick={() => history.push("/blogs")}
          >
            Blogs
          </button>
        </nav>
        <div className="lr-nav__actions">
          {isLoggedIn ? (
            <div ref={avatarAnchorRef} className="lr-nav__avatar">
              <Profile setOpenSettings={setOpenSettings} anchor={avatarAnchorRef} />
            </div>
          ) : (
            <button type="button" className="lr-nav__signin" onClick={() => setOpenSignIn(true)}>
              Sign in
            </button>
          )}
          <button type="button" className="lr-nav__get-started" onClick={handleStarted}>
            {t("watermarkNow")}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <SignIn open={openSignIn} setOpen={setOpenSignIn} />
      <SettingsModal
        open={openSettings}
        setOpen={setOpenSettings}
        handleDone={() => setOpenSettings(false)}
        parent={false}
      />
    </header>
  );
};

export default Nav;
